import type { MouseEvent } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { SessionWorkItem } from '@localagent/sdk';
import { isSessionRunning } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { SessionWorkItemBadge } from '../work/SessionWorkItemBadge';
import { formatPersonaName } from '../../utils/persona-display';
import type { AgentSession } from '../../types/session-types';

interface SessionRowProps {
  session: AgentSession;
  workItems: SessionWorkItem[] | undefined;
  isActive: boolean;
  needsInput: boolean;
  selecting: boolean;
  selected: boolean;
  onToggleSelect: (sessionId: string) => void;
  onSelect: (sessionId: string) => void;
  onDelete: (
    sessionId: string,
    workspaceId: string | null | undefined,
    event: MouseEvent,
  ) => void;
  deleting: boolean;
}

export function SessionRow({
  session,
  workItems,
  isActive,
  needsInput,
  selecting,
  selected,
  onToggleSelect,
  onSelect,
  onDelete,
  deleting,
}: SessionRowProps) {
  const running = session.running || isSessionRunning(workItems);

  return (
    <div
      onClick={() => onSelect(session.id)}
      data-testid={`session-item-${session.id}`}
      data-session-running={running}
      data-needs-input={needsInput}
      data-active={isActive}
      className={`group flex cursor-pointer items-center justify-between rounded px-3 py-1.5 text-label transition-colors hover:bg-accent`}
    >
      {selecting && (
        <input
          type="checkbox"
          className="mr-2 shrink-0"
          checked={selected}
          data-testid={`session-select-${session.id}`}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggleSelect(session.id)}
          aria-label={`Select ${session.name}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {running && (
            <span
              data-testid={`session-activity-${session.id}`}
              title="This session's agent is working"
              aria-label="Working"
              className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary"
            />
          )}
          <div className={`truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>{session.name}</div>
          {needsInput && (
            <span
              data-testid={`session-needs-input-${session.id}`}
              title="An agent in this session needs your input"
              aria-label="Needs your input"
              className="ml-auto shrink-0 rounded-sm bg-warning-subtle px-1 text-micro text-warning-text"
            >
              ?
            </span>
          )}
        </div>
        <div className="truncate text-micro text-muted-foreground">
          {session.persona_name && `${formatPersonaName(session.persona_name)} · `}
          {session.last_accessed &&
            formatDistanceToNow(new Date(session.last_accessed), { addSuffix: true })}
        </div>
        {workItems?.[0] && <SessionWorkItemBadge item={workItems[0]} />}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="ml-1 hidden h-6 w-6 shrink-0 p-0 group-hover:inline-flex"
        onClick={(e) => onDelete(session.id, session.workspace_id, e)}
        disabled={deleting}
        data-testid={`delete-session-${session.id}`}
      >
        ×
      </Button>
    </div>
  );
}
